import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { cn } from '@/lib/cn';

/**
 * Pengurai CSV kecil yang menghormati tanda kutip (RFC 4180 secukupnya).
 *
 * split(',') saja akan merusak sel yang berisi koma - dan data scraping penuh
 * dengan itu: judul produk, alamat, kalimat kutipan. Pengurai 30 baris ini
 * menangani kutip ganda dan koma di dalam sel tanpa menyeret dependensi baru.
 */
const parseCsv = (teks, maxBaris = 200) => {
  const baris = [];
  let sel = '';
  let barisIni = [];
  let dalamKutip = false;

  for (let i = 0; i < teks.length; i += 1) {
    const ch = teks[i];
    if (dalamKutip) {
      if (ch === '"') {
        if (teks[i + 1] === '"') {
          sel += '"';
          i += 1;
        } else {
          dalamKutip = false;
        }
      } else {
        sel += ch;
      }
    } else if (ch === '"') {
      dalamKutip = true;
    } else if (ch === ',') {
      barisIni.push(sel);
      sel = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && teks[i + 1] === '\n') i += 1;
      barisIni.push(sel);
      sel = '';
      if (barisIni.some((s) => s !== '')) baris.push(barisIni);
      barisIni = [];
      if (baris.length > maxBaris) break;
    } else {
      sel += ch;
    }
  }
  if (sel !== '' || barisIni.length) {
    barisIni.push(sel);
    if (barisIni.some((s) => s !== '')) baris.push(barisIni);
  }
  return baris;
};

const stringkan = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/** Mengubah isi pratinjau menjadi {header, rows} sesuai formatnya. */
const keTabel = (format, content) => {
  try {
    if (format === 'csv') {
      const baris = parseCsv(content);
      if (baris.length === 0) return null;
      return { header: baris[0], rows: baris.slice(1, 201) };
    }
    if (format === 'json') {
      const data = JSON.parse(content);
      if (!Array.isArray(data) || data.length === 0) return null;
      const header = [...new Set(data.slice(0, 50).flatMap((r) => Object.keys(r)))];
      return { header, rows: data.slice(0, 200).map((r) => header.map((h) => stringkan(r[h]))) };
    }
    if (format === 'ndjson') {
      const objek = content
        .split('\n')
        .filter((b) => b.trim())
        .slice(0, 200)
        .map((b) => JSON.parse(b));
      if (objek.length === 0) return null;
      const header = [...new Set(objek.slice(0, 50).flatMap((r) => Object.keys(r)))];
      return { header, rows: objek.map((r) => header.map((h) => stringkan(r[h]))) };
    }
  } catch {
    // Isi yang rusak atau terpotong di tengah membuat parse gagal; tampilan
    // mentah tetap tersedia, jadi kegagalan di sini bukan jalan buntu.
    return null;
  }
  return null;
};


/**
 * Pratinjau isi berkas: tampilan mentah apa adanya, atau tabel seperti
 * lembar kerja. Untuk berkas besar hanya potongan awalnya yang ditarik.
 */
export const PreviewDialog = ({ file, onClose }) => (
  <PratinjauUmum
    judul={file?.filename}
    kunciQuery={['file-preview', file?.id]}
    jalur={`/storage/files/${file?.id}/preview`}
    aktif={Boolean(file)}
    onClose={onClose}
  />
);

/**
 * Pratinjau isi dataset publik. Sumbernya berbeda dari berkas pribadi -
 * izinnya diperiksa lewat dataset, bukan kepemilikan berkas - tetapi
 * tampilannya persis sama.
 */
export const DatasetPreviewDialog = ({ dataset, onClose }) => (
  <PratinjauUmum
    judul={dataset?.title}
    kunciQuery={['dataset-preview', dataset?.slug]}
    jalur={`/datasets/${dataset?.slug}/preview`}
    aktif={Boolean(dataset)}
    onClose={onClose}
  />
);

const PratinjauUmum = ({ judul, kunciQuery, jalur, aktif, onClose }) => {
  const [modeTabel, setModeTabel] = useState(true);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: kunciQuery,
    queryFn: () => api.get(jalur),
    enabled: aktif,
  });

  const pratinjau = data?.data;
  const tabel = useMemo(
    () => (pratinjau?.previewable ? keTabel(pratinjau.format, pratinjau.content) : null),
    [pratinjau],
  );

  if (!aktif) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={judul}
      description={
        pratinjau?.truncated
          ? 'Menampilkan potongan awal berkas. Unduh untuk isi lengkapnya.'
          : 'Isi lengkap berkas.'
      }
      size="lg"
    >
      {isLoading ? (
        <PageLoader label="Membaca berkas..." />
      ) : isError ? (
        <p className="py-6 text-center text-sm text-danger-500">
          {error?.message ?? 'Isi berkas tidak bisa dibaca.'}
        </p>
      ) : !pratinjau?.previewable ? (
        <p className="py-6 text-center text-sm text-[var(--text-muted)]">
          Berkas {pratinjau?.format ?? 'ini'} berbentuk biner dan tidak bisa dipratinjau.
          Unduh untuk membukanya di aplikasi lembar kerja.
        </p>
      ) : (
        <div className="space-y-3">
          <div
            role="tablist"
            aria-label="Mode tampilan"
            className="inline-flex gap-1 rounded-lg bg-[var(--surface-sunken)] p-1"
          >
            {[
              [true, 'Tabel'],
              [false, 'Mentah'],
            ].map(([nilai, label]) => (
              <button
                key={label}
                role="tab"
                type="button"
                aria-selected={modeTabel === nilai}
                onClick={() => setModeTabel(nilai)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  modeTabel === nilai
                    ? 'bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-xs'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {modeTabel && tabel ? (
            <div className="max-h-[55vh] overflow-auto rounded-lg border border-[var(--border-subtle)]">
              <TableWrapper>
                <Table>
                  <thead>
                    <Tr>
                      {tabel.header.map((h, i) => (
                        <Th key={i}>{h}</Th>
                      ))}
                    </Tr>
                  </thead>
                  <tbody>
                    {tabel.rows.map((baris, i) => (
                      <Tr key={i}>
                        {baris.map((sel, j) => (
                          <Td key={j} className="max-w-64 truncate" title={sel}>
                            {sel}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            </div>
          ) : modeTabel ? (
            <p className="py-4 text-center text-sm text-[var(--text-muted)]">
              Isi berkas tidak bisa dibaca sebagai tabel; lihat tampilan Mentah.
            </p>
          ) : (
            <pre className="max-h-[55vh] overflow-auto rounded-lg bg-[var(--surface-sunken)] p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-[var(--text-secondary)]">
              {pratinjau.content}
            </pre>
          )}
        </div>
      )}
    </Modal>
  );
};
